<template>
    <div class="prize-page">
        <div v-if="selectedFaceCard" class="facecard-container">
            <ContainerObject :containerObject="selectedFaceCard" @event="customEvent($event)" />
        </div>
        <div v-else-if="!loading" class="no-facecard">
            <p>No facecard available for this prize</p>
        </div>
        <LoadingComponent v-if="loading" />
    </div>
</template>

<script lang="ts">
import { defineComponent } from "vue"
import { Container } from "@/types/customPage"
import { VItem, ItemStatus } from "@/types/vItem"
import { Prize, RedemptionType } from "@/types/prize"
import { emptyContainerWithMessage } from "@/utils/container"
import { vItemStore } from "@/store/pinia_modules/vItemStore"
import { authStore } from "@/store/pinia_modules/authStore"
import { prizeStore } from "@/store/pinia_modules/prizeStore"
import { dropPoolStore } from "@/store/pinia_modules/dropPoolStore"
import { categoryStore } from "@/store/pinia_modules/categoryStore"
import { campaignStore } from "@/store/pinia_modules/campaignStore"

export default defineComponent({
    data() {
        return {
            selectedFaceCard: null as Container | null,
            selectedItem: null as VItem | null,
            loading: true,
        }
    },
    async mounted() {
        await this.initializePrize()
    },
    watch: {
        selectedItem: {
            handler(newVal, oldVal) {
                if (newVal && newVal.id !== oldVal?.id) {
                    this.initializePrize()
                }
            },
            immediate: true
        }
    },
    methods: {
        async initializePrize() {
            this.loading = true

            // Validate campaign ID first
            const campaignId = this.$route.params.campaign as string
            if (!campaignId || campaignId === 'error') {
                console.error("Invalid campaign ID:", campaignId)
                this.loading = false
                return
            }

            if (campaignStore().campaign?.id !== campaignId) {
                let campaignIdentifier: {
                    subdomain: string | null;
                    campaignId: string | null;
                } = { subdomain: null, campaignId: null };

                if (campaignId) {
                    campaignIdentifier.campaignId = campaignId;
                }
                console.log("PrizePage: campaignIdentifier", campaignIdentifier)
                const campaignData = await campaignStore().getInitialCampaignData(campaignIdentifier)
                campaignStore().setCampaign(campaignData.campaign)
            }

            // Get vitem ID from route params
            const vitemID = this.$route.params.vitemID as string
            if (!vitemID) {
                console.error("No vitem ID provided")
                this.loading = false
                return
            }

            // Find the vitem
            await vItemStore().getItems()
            const item = vItemStore().items.find(item => item.id === vitemID)
            if (!item) {
                console.error("VItem not found:", vitemID)
                this.loading = false
                return
            }

            this.selectedItem = item
            vItemStore().selectItem(item.id!)

            await prizeStore().getPrizes()
            // Get the prize and determine which facecard to show
            const prize = this.getPrizeByID(item.prizeId!)
            if (!prize) {
                console.error("Prize not found:", item.prizeId)
                this.loading = false
                return
            }

            // Determine by vitem status
            switch (item.itemStatus) {
                case ItemStatus.CLAIMED:
                    await this.setupGameplayFacecard(prize)
                    break
                case ItemStatus.ALLOCATED:
                    await this.setupRedemptionFacecard(prize)
                    break
                case ItemStatus.NOPRIZE:
                    let popupname = "already-redeemed"
                    switch (prize.redemptionType) {
                        case RedemptionType.VOUCHER:
                            popupname = "already-redeemed-voucher"
                            break
                        case RedemptionType.TAP_REDEEM:
                            popupname = "already-redeemed-tap-to-redeem"
                            break
                        case RedemptionType.SCAN:
                            popupname = "already-redeemed-qr"
                            break
                        case RedemptionType.LINK_VOUCHER:
                            popupname = "already-redeemed-url-voucher"
                            break
                        case RedemptionType.YOYO_COUPON:
                            popupname = "already-redeemed-yoyo-coupon"
                            break
                        case RedemptionType.MOMINT_NFT:
                            popupname = "already-redeemed-momint-nft"
                            break
                    }
                    this.$router.push(`/${authStore().getCampaignID!}/vault?popup=${popupname}`)
                    break
                case ItemStatus.REDEEMED:
                    await this.setupRedemptionFacecard(prize)
                    break
                default:
                    console.error("Unknown vitem status:", item.itemStatus)
                    break
            }

            this.loading = false
        },

        async setupGameplayFacecard(prize: Prize) {
            // Set up click to play state
            if (prize.clickToPlay) {
                prizeStore().saveClickToPlay(prize.clickToPlay)
            } else {
                prizeStore().saveClickToPlay(false)
            }

            // Determine winning/losing state
            const pool = this.selectedItem?.dropPoolId ? this.getPoolByID(this.selectedItem.dropPoolId) : null

            if (pool && pool?.autoScalingPrizeStrategy && !this.selectedItem?.winning) {
                prizeStore().saveIsWinning(false)
                this.selectedFaceCard = prize.losingGameplayFaceCard || emptyContainerWithMessage("No Facecard", "Losing facecard not configured")
            } else {
                prizeStore().saveIsWinning(true)
                this.selectedFaceCard = prize.gameplayFaceCard || emptyContainerWithMessage("No Facecard", "Gameplay facecard not configured")
            }
        },

        async setupRedemptionFacecard(prize: Prize) {
            this.selectedFaceCard = prize.redemptionFaceCard as Container
            if (prize.isWinningPrize) {
                this.populateVoucherCode()
            } else {
                await vItemStore().setNoPrize()
                await vItemStore().getItems()
            }
        },

        async customEvent($event: any) {
            console.log("Prize.vue customEvent received:", $event, "Item status:", this.selectedItem?.itemStatus)

            if ($event == "redeem" && this.selectedItem) {
                // Only allow redemption if item is in CLAIMED status (not already ALLOCATED/REDEEMED)
                if (this.selectedItem.itemStatus === ItemStatus.ALLOCATED) {
                    console.log("Processing redeem action for item:", this.selectedItem.id)
                    this.setupRedemptionFacecard(this.getPrizeByID(this.selectedItem.prizeId)!);
                } else {
                    console.warn("Cannot redeem item - item status is:", this.selectedItem.itemStatus)
                }
            } else if ($event == "losingPrize" && this.selectedItem) {
                await vItemStore().getItems()
                const campaignId = authStore().getCampaignID
                this.$router.push(`/${campaignId}/vault`)
            } else if ($event == "close" || $event == "back") {
                // Navigate back to vault
                const campaignId = authStore().getCampaignID
                this.$router.push(`/${campaignId}/vault`)
            }
        },

        populateVoucherCode() {
            setTimeout(() => {
                const voucherCodes = document.querySelectorAll(
                    ".voucher-code"
                ) as any
                const voucherCode = document.querySelector(
                    "#voucher-code"
                ) as HTMLElement
                if (voucherCode) {
                    voucherCode.innerText = this.selectedItem!.voucherCode!
                }
                if (voucherCodes) {
                    for (let i = 0; i < voucherCodes.length; i++) {
                        voucherCodes[i].innerText =
                            this.selectedItem!.voucherCode!
                    }
                }
            }, 100)
        },


        getPrizeByID(id: string): Prize | undefined {
            return prizeStore().getPrizeByID(id)
        },

        getPoolByID(id: string) {
            return dropPoolStore().getPoolByID(id)
        },

        getCategoryByID(id: string) {
            return categoryStore().getItemByID(id)
        },
    },
})
</script>

<style scoped lang="scss">
.prize-page {
    position: fixed;
    display: flex;
    width: 100%;
    height: 100%;
    z-index: 100;
    top: 0;
    left: 0;
    background-color: #fff;
    justify-content: center;
    align-items: center;
}

.facecard-container {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
}

.no-facecard {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;

    p {
        font-size: 18px;
        color: #666;
        text-align: center;
        margin: 20px;
    }
}
</style>
